import React, { useState, useEffect, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import projectLogo from "../beast-logo.png";
import { serviceApi } from "../service";
import InfoTable from "../components/InfoTable.jsx";
import BeastNavbar from "../components/BeastNavbar.jsx";
import { AtomStructure, Atom } from "../AtomicStructure";
import { getPath } from "../utils";
import { CatalystPathPlot } from "../components/CatalystPathPlot.jsx";
import { ReactionDataTable } from "../components/ReactionDataTable.jsx";
import { RelatedReactionTable } from "../components/RelatedReactionTable.jsx";
import downloadIcon from "../download.png";
import { stateIndices } from "../ReactionPaths.js";

import BindingEnergyDataset from "../Datasets.js";

import Stack from "react-bootstrap/Stack";
import Figure from "react-bootstrap/Figure";
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";
import Navbar from "react-bootstrap/Navbar";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Col from "react-bootstrap/Col";

import Chart from "chart.js/auto";

import "../x3dom.css";

const CatalystPathPage = () => {
  const [data, setData] = useState([]);
  const [plotInfo, setPlotInfo] = useState([]);
  const [columnUnits, setColumnUnits] = useState(null);
  const [relatedData, setRelatedData] = useState([]);

  let params = useParams();

  useLayoutEffect(() => {
    document.body.style.backgroundColor = "#F3F3F3";
  });

  // Fetch units asynchronously on page load
  useEffect(() => {
    serviceApi
      .columns()
      .then((result) => {
        setColumnUnits(result.units);
      })
      .catch((error) => {
        console.error("Error fetching units from API:", error);
      });
  }, []);

  // Fetch data asynchronously on page load
  useEffect(() => {
    serviceApi
      .unfilteredQuery(
        {
          query: "reaction",
          unique_id: params.reactionId,
        },
        "system_name",
      )
      .then((reactionResult) => {
        if (reactionResult.result.length > 0) {
          let catalystFacet = reactionResult.result[0].reaction.catalyst_facet;
          let catalystFormula =
            reactionResult.result[0].reaction.catalyst_formula;
          let reactionType = reactionResult.result[0].reaction.reaction_type;
          setPlotInfo({
            catalystFacet: catalystFacet,
            catalystFormula: catalystFormula,
            reactionType: reactionType,
          });
          serviceApi
            .unfilteredQuery(
              {
                query: "reaction",
                "reaction.catalyst_facet": catalystFacet,
                "reaction.catalyst_formula": catalystFormula,
                "reaction.reaction_type": reactionType,
              },
              "system_name",
            )
            .then((relatedReactionsResult) => {
              setData(relatedReactionsResult.result);
            })
            .catch((error) => {
              console.error("Error fetching data from API:", error);
            });
          let reactionQueryObj = {
            query: "reaction",
            "reaction.catalyst_formula": catalystFormula,
            "reaction.catalyst_facet": catalystFacet,
          };
          serviceApi
            .unfilteredQuery(reactionQueryObj, "system_name")
            .then((reactionResult) => {
              let relatedReactions = [];
              reactionResult.result.forEach((reaction) => {
                relatedReactions.push(reaction);
              });
              if (relatedReactions.length > 0) {
                setRelatedData(relatedReactions);
              }
            })
            .catch((error) => {
              console.error("Error fetching data from API:", error);
            });
        }
      })
      .catch((error) => {
        console.error("Error fetching data from API:", error);
      });
  }, [params]);

  function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  let reactions = relatedData
    .map((reaction) => {
      return reaction.reaction.reaction_type;
    })
    .filter(onlyUnique);

  var relatedReactionsCard = <div></div>;
  if (reactions.length > 1) {
    relatedReactionsCard = (
      <Card className="shadow-sm pt-3">
        <Row>
          <Card.Title className="mx-4 my-4">Related Reactions</Card.Title>
        </Row>
        <RelatedReactionTable relatedReactionData={relatedData} />
      </Card>
    );
  }

  let plotTitle =
    plotInfo.reactionType +
    " Reaction for " +
    plotInfo.catalystFormula +
    " (" +
    plotInfo.catalystFacet +
    ")";

  if (data?.length != 0) {
    var plotCard = (
      <Card className="shadow-sm p-3">
        <Card.Title className="pt-3">{plotTitle}</Card.Title>
        <CatalystPathPlot
          plotId="catalyst-path-chart"
          plotData={data}
          surface={params.surface}
          facet={params.facet}
          reaction={params.reaction}
        />
      </Card>
    );
  } else {
    var plotCard = "";
  }

  var saveData = (function () {
    var a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    return function (data, fileName) {
      var blob = new Blob([data], { type: "octet/stream" }),
        url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    };
  })();

  function saveCSV() {
    let steps = stateIndices[data[0].reaction.reaction_type];
    let potentials = data.map((reaction) => {
      return reaction.reaction.potential;
    });
    let csvData = ["State", ...potentials].join(",");
    for (let state in steps) {
      let step = steps[state];
      csvData +=
        "\n" +
        [
          step,
          ...potentials.map(
            (potential, potential_i) =>
              data[potential_i].reaction.steps[step].energies[0],
          ),
        ].join(",");
    }
    csvData +=
      "\n" +
      [
        "Limiting Potential",
        ...potentials.map(
          (potential, potential_i) =>
            data[potential_i].reaction.limiting_potential,
        ),
      ].join(",");
    csvData +=
      "\n" +
      [
        "Energetic Span",
        ...potentials.map(
          (potential, potential_i) => data[potential_i].reaction.energetic_span,
        ),
      ].join(",");
    saveData(csvData, `BEAST_DB_${params.reactionId}.csv`);
  }

  return (
    <div className="App">
      <BeastNavbar selected="reaction" />
      <div class="wrapper">
        <Container className="py-3" fluid="md">
          <Stack direction="vertical" gap={3}>
            {plotCard}
            {relatedReactionsCard}
            <Card className="shadow-sm pt-3">
              <Row>
                <Col></Col>
                <Col xs={6} sm={6} lg={6}>
                  <Card.Title className="mx-4 my-4">
                    Reaction Data (eV)
                  </Card.Title>
                </Col>
                <Col xs={4} sm={3} md={4} lg={3} className="px-4 py-4">
                  <Button
                    onClick={saveCSV}
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title="Download"
                  >
                    <img
                      src={downloadIcon}
                      width={24}
                      height={24}
                      alt="Download"
                    />
                  </Button>
                </Col>
              </Row>
              <ReactionDataTable reactionData={data} />
            </Card>
          </Stack>
        </Container>
      </div>
    </div>
  );
};

export default CatalystPathPage;
